import { motion } from "framer-motion";
import { Navbar, FinalCTA, Waveform, fadeUp, staggerContainer, btnMotion } from "../components/Shared";

const BENEFITS = [
  { 
    title: "Better Access to Support", 
    desc: "Replace the forgotten EAP hotline with an integrated, modern experience employees actually use." 
  },
  { 
    title: "Manager Enablement", 
    desc: "Equip your frontline leaders with the frameworks and scripts to support a struggling team member without acting as a therapist." 
  },
  { 
    title: "Organizational Visibility", 
    desc: "Move from reactive crisis management to proactive structural improvement with aggregate, anonymized wellbeing data." 
  },
  { 
    title: "Continuous Behavioral Support", 
    desc: "Bridge the gap between one-off wellness webinars and long-term resilience building through personalized coaching." 
  }
];

const STEPS = [
  "1. Discovery & Alignment: We map your current wellbeing gaps and employee demographics.",
  "2. Seamless Integration: SSO setup and communication rollout in under 2 weeks.",
  "3. Launch & Engage: Employees gain immediate, private access to coaching and stress-regulation modules.",
  "4. Quarterly Insights: Leadership receives anonymized trend reports and strategic recommendations."
];

export default function ForOrganizations() {
  return (
    <main className="cw-organizations">
      <Navbar />
      
      <section className="cw-section" style={{ paddingTop: 'var(--space-xl)', textAlign: 'center' }}>
        <div className="cw-section__inner">
          <motion.div variants={staggerContainer(0.1)} initial="hidden" animate="visible">
            <motion.p className="cw-eyebrow" variants={fadeUp}>For Organizations</motion.p>
            <motion.h1 variants={fadeUp} style={{ fontSize: '3rem', marginBottom: 'var(--space-md)' }}>
              Stop buying fragmented wellbeing vendors.
            </motion.h1>
            <motion.p variants={fadeUp} style={{ fontSize: '1.2rem', maxWidth: '650px', margin: '0 auto var(--space-md)', opacity: 0.8 }}>
              Cognito is the all-in-one corporate wellbeing and performance platform. We combine employee support, manager enablement, and organizational insight into a single, measurable system.
            </motion.p>
            <div style={{ display: 'flex', justifyContent: 'center' }}><Waveform /></div>
            <motion.a 
              href="mailto:cs@cognitowellness.org?subject=Pilot Inquiry: Cognito Wellness" 
              className="cw-btn cw-btn--primary" 
              variants={fadeUp} 
              {...btnMotion}
            >
              Start a 3-Month Pilot
            </motion.a>
          </motion.div>
        </div>
      </section>

      <section className="cw-section cw-section--alt">
        <div className="cw-section__inner">
          <motion.p className="cw-eyebrow" style={{ textAlign: 'center' }}>The Cognito Advantage</motion.p>
          <motion.h2 className="cw-section__title" style={{ textAlign: 'center', fontSize: '2.2rem', marginBottom: 'var(--space-lg)' }}>
            Why we are more than a traditional EAP.
          </motion.h2>
          <motion.div variants={staggerContainer(0.1)} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-md)' }}>
            {BENEFITS.map((b) => (
              <motion.div key={b.title} className="cw-card" variants={fadeUp}>
                <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.3rem', marginBottom: 'var(--space-xs)' }}>{b.title}</h3>
                <p style={{ opacity: 0.8, fontSize: '0.95rem' }}>{b.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="cw-section">
        <div className="cw-section__inner" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-xl)' }}>
          <motion.div variants={staggerContainer(0.1)} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.p className="cw-eyebrow" variants={fadeUp}>Implementation</motion.p>
            <motion.h2 variants={fadeUp} style={{ fontSize: '2.2rem', marginBottom: 'var(--space-md)' }}>
              Onboarding that respects your time.
            </motion.h2>
            <motion.ul variants={staggerContainer(0.1)} style={{ listStyle: 'none', display: 'grid', gap: 'var(--space-md)' }}>
              {STEPS.map((step, i) => (
                <motion.li key={i} className="cw-card" variants={fadeUp} style={{ borderLeft: '3px solid var(--cw-teal)' }}>
                  {step}
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
          
          <motion.div 
            className="cw-card" 
            variants={fadeUp} 
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true }} 
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'var(--cw-navy)', color: 'var(--cw-offwhite)', padding: 'var(--space-lg)' }}
          >
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', marginBottom: 'var(--space-sm)' }}>Enterprise-Grade Privacy</h3>
            <p style={{ opacity: 0.8, marginBottom: 'var(--space-md)', lineHeight: 1.7, fontSize: '0.95rem' }}>
              We operate on a strict "need-to-know" aggregate model. Your organization funds the platform and receives macro-level insights to improve culture, but individual confidentiality remains absolute. 
            </p>
            <p style={{ opacity: 1, fontWeight: '600', color: 'var(--cw-teal)', fontSize: '0.95rem' }}>
              We do not record sessions, and individual identities are never exposed to leadership.
            </p>
          </motion.div>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}